import type { Dimension, MainScore } from "src/api/client-api/olapScores/olapScoresSchema";
import { OlapLocalizedLabelContext, OlapLocalizedLabelService } from "src/context/OlapScores/OlapLocalizedLabelService";
import { OlapHeadingColorService } from "src/context/OlapScores/OlapHeadingColorService";
import { FlattenedDimensionValue, OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { URLHandler } from "src/services/URLHandler";

// Interface minimale nécessaire pour la navigation
interface CompanyForNavigation {
    id: number;
    organizationSector?: {
        ssqs?: Array<{ ssqId: number; position?: number }>;
        primarySsqId?: number;
        ssqId?: number;
        [key: string]: any; // Pour permettre d'autres propriétés
    };
}

/**
 * Détermine l'URL de navigation appropriée pour les questionnaires selon le nombre de QSS disponibles
 */
export const getQuestionnaireNavigationUrl = (company: CompanyForNavigation, currentCampaignId?: number): string => {
    if (!currentCampaignId) {
        throw new Error("Campaign ID is required for questionnaire navigation");
    }

    // Récupérer les QSS disponibles depuis le secteur d'activité
    const availableSsqs = company.organizationSector?.ssqs || [];

    // Si aucun QSS disponible mais qu'il y a un ssqId primaire, l'utiliser
    if (availableSsqs.length === 0 && company.organizationSector?.primarySsqId) {
        return URLHandler.company.ssq(company.id, company.organizationSector.primarySsqId, currentCampaignId);
    }

    // Si aucun QSS disponible et pas de primarySsqId, mais qu'il y a ssqId, l'utiliser
    if (availableSsqs.length === 0 && company.organizationSector?.ssqId) {
        return URLHandler.company.ssq(company.id, company.organizationSector.ssqId, currentCampaignId);
    }

    // Si aucun QSS disponible du tout, rediriger vers la page de sélection pour afficher l'erreur
    if (availableSsqs.length === 0) {
        return URLHandler.company.questionnaire(company.id);
    }

    // Si un seul QSS, rediriger directement vers le questionnaire
    if (availableSsqs.length === 1) {
        const ssqId = availableSsqs[0].ssqId;
        return URLHandler.company.ssq(company.id, ssqId, currentCampaignId);
    }

    // Si plusieurs QSS, rediriger vers la page de sélection
    return URLHandler.company.questionnaire(company.id);
};

/**
 * Vérifie si une société a plusieurs QSS disponibles
 */
export const hasMultipleSsqs = (company: CompanyForNavigation): boolean => {
    const availableSsqs = company.organizationSector?.ssqs || [];
    return availableSsqs.length > 1;
};

/**
 * Récupère les QSS disponibles pour une société
 */
export const getAvailableSsqs = (company: CompanyForNavigation): Array<{ ssqId: number; position?: number }> => {
    return company.organizationSector?.ssqs || [];
};

export const getChartData = (
    mainScore: MainScore | undefined,
    dimension: string = "headings",
    localizedLabelContext?: OlapLocalizedLabelContext,
) => {
    const emptyChartData = { data: { values: [], labels: [] }, totalScore: 0, maxScore: [], specificityRate: null };

    if (!mainScore) {
        return emptyChartData;
    }

    const specificityRateScore = OlapScoresService.getSubscoreWithType("specificity-rate", mainScore);
    const gradeSubscore = OlapScoresService.getSubscoreWithDisplayMode("grade", mainScore);

    const totalScore = OlapScoresService.findDimensionValueWithDimension(dimension, mainScore.dimensions) ?? mainScore;
    const totalGradeScore =
        OlapScoresService.findDimensionValueWithDimension(dimension, gradeSubscore?.dimensions) ?? gradeSubscore;

    if (!totalScore) {
        return emptyChartData;
    }

    const scoresDimension = OlapScoresService.getDimension(dimension, totalScore.dimensions);
    const gradeDimension = OlapScoresService.getDimension(dimension, totalGradeScore?.dimensions);

    const flattenedScores = OlapScoresService.flattenDimensionValues(scoresDimension);
    const flattenedGradeScores = OlapScoresService.flattenDimensionValues(gradeDimension);

    return {
        data: {
            values: flattenedScores.map((value: FlattenedDimensionValue) =>
                OlapScoresService.normalizeNumericalScore(value.score, 0),
            ),
            labels: flattenedScores.map(
                (value: FlattenedDimensionValue) =>
                    OlapLocalizedLabelService.localize(dimension, value.id, localizedLabelContext) ?? value.label,
            ),
        },
        totalScore: OlapScoresService.normalizeNumericalScore(totalScore?.score.value, 0),
        maxScore: flattenedGradeScores.map((value: FlattenedDimensionValue) =>
            OlapScoresService.normalizeNumericalScore(value.score, 0),
        ),
        specificityRate: OlapScoresService.normalizeNumericalScore(specificityRateScore?.score.value),
    };
};

export const getSameLevelDimensionsChartData = (
    mainScore: MainScore | undefined,
    dimensions: readonly string[],
    localizedLabelContext?: OlapLocalizedLabelContext,
) => {
    const shouldExcludeVersionedHeadings = dimensions.includes("headings") && dimensions.includes("ssq_versions");
    const excludedHeadingIds =
        shouldExcludeVersionedHeadings ?
            getNestedDimensionValueIds(mainScore?.dimensions, "ssq_versions", "headings")
        :   new Set<string>();
    const chartData = dimensions.map((dimension) =>
        getTopLevelChartData(mainScore, dimension, excludedHeadingIds, localizedLabelContext),
    );

    const primaryChartData = chartData[0] ?? {
        data: { values: [], labels: [] },
        totalScore: 0,
        maxScore: [],
        specificityRate: null,
    };

    return {
        data: {
            values: chartData.flatMap(({ data }) => data.values),
            labels: chartData.flatMap(({ data }) => data.labels),
            colors: OlapHeadingColorService.resolveMany(
                chartData.flatMap(({ dimensionValues }) => dimensionValues),
                localizedLabelContext,
            ),
        },
        totalScore: OlapScoresService.normalizeNumericalScore(mainScore?.score.value, 0),
        maxScore: chartData.flatMap(({ maxScore }) => maxScore),
        specificityRate: primaryChartData.specificityRate,
    };
};

const getTopLevelChartData = (
    mainScore: MainScore | undefined,
    dimension: string,
    excludedValueIds: Set<string>,
    localizedLabelContext?: OlapLocalizedLabelContext,
) => {
    const emptyChartData = {
        data: { values: [], labels: [] },
        dimensionValues: [],
        totalScore: 0,
        maxScore: [],
        specificityRate: null,
    };

    if (!mainScore) {
        return emptyChartData;
    }

    const specificityRateScore = OlapScoresService.getSubscoreWithType("specificity-rate", mainScore);
    const gradeSubscore = OlapScoresService.getSubscoreWithDisplayMode("grade", mainScore);
    const scoresDimension = OlapScoresService.getDimension(dimension, mainScore.dimensions);
    const gradeDimension = OlapScoresService.getDimension(dimension, gradeSubscore?.dimensions);
    const flattenedScores = OlapScoresService.flattenDimensionValues(scoresDimension).filter(
        (value) => dimension !== "headings" || !excludedValueIds.has(value.id),
    );
    const flattenedGradeScores = OlapScoresService.flattenDimensionValues(gradeDimension).filter(
        (value) => dimension !== "headings" || !excludedValueIds.has(value.id),
    );

    return {
        data: {
            values: flattenedScores.map((value: FlattenedDimensionValue) =>
                OlapScoresService.normalizeNumericalScore(value.score, 0),
            ),
            labels: flattenedScores.map(
                (value: FlattenedDimensionValue) =>
                    OlapLocalizedLabelService.localize(dimension, value.id, localizedLabelContext) ?? value.label,
            ),
        },
        dimensionValues: flattenedScores.map((value: FlattenedDimensionValue) => ({ dimension, id: value.id })),
        totalScore: OlapScoresService.normalizeNumericalScore(mainScore.score.value, 0),
        maxScore: flattenedGradeScores.map((value: FlattenedDimensionValue) =>
            OlapScoresService.normalizeNumericalScore(value.score, 0),
        ),
        specificityRate: OlapScoresService.normalizeNumericalScore(specificityRateScore?.score.value),
    };
};

const getNestedDimensionValueIds = (
    dimensions: Dimension[] | undefined,
    parentDimensionName: string,
    nestedDimensionName: string,
): Set<string> => {
    const parentDimension = OlapScoresService.getDimension(parentDimensionName, dimensions);
    const ids = new Set<string>();

    parentDimension?.values.forEach((value) => {
        findDimensionValueIds(value.dimensions, nestedDimensionName).forEach((id) => ids.add(id));
    });

    return ids;
};

const findDimensionValueIds = (dimensions: Dimension[] | undefined, dimensionName: string): string[] => {
    return (
        dimensions?.flatMap((dimension) => {
            const nestedIds = dimension.values.flatMap((value) =>
                findDimensionValueIds(value.dimensions, dimensionName),
            );

            if (dimension.label !== dimensionName) {
                return nestedIds;
            }

            return [...dimension.values.map((value) => value.id), ...nestedIds];
        }) ?? []
    );
};
